import { shuffleSeedForPlaylist } from "./seeded-shuffle.ts";
import { materializeRotation, type RotationMode } from "./rotation.ts";
import type { SyncSegment } from "./sync-group.ts";

export const DEFAULT_TRACK_DURATION_SEC = 180;
/** Shared default on-screen duration for still images without a configured length. */
export const IMAGE_DEFAULT_DISPLAY_SEC = 10;
export const MAX_TIMELINE_SEGMENTS = 48;
/**
 * Hard per-channel ceiling for horizon-scaled timelines (CB-091): the default
 * 7-day offline window at the 180s default track duration. Keeps a
 * short-item rotation (e.g. 10s stills) from exploding the payload — such a
 * channel's timeline covers less wall clock than the horizon; past its end,
 * devices fall back to the seeded local rotation (see docs/player-offline.md).
 */
export const OFFLINE_MAX_TIMELINE_SEGMENTS = 3360;

/**
 * Per-channel segment cap for a sync horizon: enough 180s-average segments to
 * cover the horizon, never below the online default (48) and never above the
 * offline ceiling. No horizon (online 30-minute syncs pass none) keeps the
 * default cap.
 */
export function timelineSegmentCapForHorizon(
  horizonMs?: number | null,
): number {
  if (horizonMs == null || !Number.isFinite(horizonMs) || horizonMs <= 0) {
    return MAX_TIMELINE_SEGMENTS;
  }
  const needed = Math.ceil(horizonMs / 1000 / DEFAULT_TRACK_DURATION_SEC);
  return Math.min(
    OFFLINE_MAX_TIMELINE_SEGMENTS,
    Math.max(MAX_TIMELINE_SEGMENTS, needed),
  );
}

/**
 * A timeline segment enriched with the exact index it occupies in the
 * playlist's materialized rotation. Weighted rotations repeat itemIds, so a
 * client aligning by itemId alone (indexOf) can land on the wrong phase of
 * the cycle — this index disambiguates. Absent on single-asset slots, which
 * have no rotation.
 */
export interface RotationSyncSegment extends SyncSegment {
  rotationIndex?: number;
}

export interface TimelineMediaItem {
  id: string;
  url?: string | null;
  durationSec?: number | null;
  type?: string;
  /** Relative weight for "weighted" rotation; absent means 1. */
  weight?: number | null;
}

export interface TimelinePlaylist {
  id: string;
  channelType: SyncSegment["channelType"];
  shuffle: boolean;
  /** Playlist rotation mode; absent falls back to the legacy shuffle flag. */
  rotationMode?: RotationMode | null;
  items: TimelineMediaItem[];
}

export interface TimelineSlot {
  channelType: SyncSegment["channelType"];
  playlistId?: string | null;
  mediaAssetId?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  /**
   * Unclipped start of the base occupancy this slot belongs to. The composed
   * now-slot's `startsAt` is clipped to the request instant, so anchoring the
   * rotation walk to it re-aligned every poll to rotation index 0 — this
   * carries the occupancy's REAL start so the projected grid is identical no
   * matter when inside the occupancy it is computed. Absent falls back to
   * `startsAt`.
   */
  occurrenceStartsAt?: string | null;
  imageDisplayDurationSec?: number | null;
}

function itemDurationSec(
  item: TimelineMediaItem,
  configuredSec?: number | null,
): number {
  if (configuredSec != null && configuredSec > 0) return configuredSec;
  if (item.durationSec != null && item.durationSec > 0) return item.durationSec;
  // Fall back to the shared still-image dwell (CB-077): the segment timeline
  // must agree with IMAGE_DEFAULT_DISPLAY_SEC so preview == playback for stills.
  if (item.type === "image") return IMAGE_DEFAULT_DISPLAY_SEC;
  return DEFAULT_TRACK_DURATION_SEC;
}

function buildRotation(
  playlist: TimelinePlaylist,
  deterministicSeed: string,
): TimelineMediaItem[] {
  const playable = playlist.items.filter((i) => Boolean(i.url));
  const mode: RotationMode =
    playlist.rotationMode ?? (playlist.shuffle ? "even" : "sequential");
  const seed = shuffleSeedForPlaylist(deterministicSeed, playlist.id);
  // The SAME materialized sequence every shell's buildRotation derives from
  // the same seed — timeline segment indices and local rotation arrays agree.
  return materializeRotation(playable, mode, seed);
}

/**
 * Locate where `elapsedMs` of continuous playback lands inside the cycled
 * rotation: the item index and the in-item offset. Whole cycles are skipped
 * arithmetically so an occupancy anchored many hours back (elapsed is
 * measured from the occupancy's real start, not the request instant) stays
 * O(rotation length). Integer-ms math keeps the derived grid byte-identical
 * across devices that compute it at different instants.
 */
function elapsedIntoRotation(
  rotation: TimelineMediaItem[],
  elapsedMs: number,
  configuredImageSec?: number | null,
): { index: number; offsetMs: number } {
  if (rotation.length === 0) return { index: 0, offsetMs: 0 };
  let remaining = Math.max(0, Math.floor(elapsedMs));
  const durations = rotation.map((item) =>
    Math.max(1, Math.round(itemDurationSec(item, configuredImageSec) * 1000)),
  );
  const cycleMs = durations.reduce((a, b) => a + b, 0);
  remaining -= Math.floor(remaining / cycleMs) * cycleMs;
  for (let idx = 0; idx < rotation.length; idx += 1) {
    if (remaining < durations[idx]!) return { index: idx, offsetMs: remaining };
    remaining -= durations[idx]!;
  }
  return { index: 0, offsetMs: 0 };
}

function appendChannelSegments(
  out: RotationSyncSegment[],
  slot: TimelineSlot,
  playlist: TimelinePlaylist | null,
  deterministicSeed: string,
  from: Date,
  maxSegments: number,
  horizonEndMs: number | null,
): void {
  if (out.length >= maxSegments) return;

  const endsAtMs = slot.endsAt ? new Date(slot.endsAt).getTime() : null;
  const slotStartMs = slot.startsAt
    ? new Date(slot.startsAt).getTime()
    : from.getTime();
  const cursorStartMs = Math.max(from.getTime(), slotStartMs);
  if (horizonEndMs != null && cursorStartMs >= horizonEndMs) return;

  if (slot.mediaAssetId) {
    const item = playlist?.items.find((i) => i.id === slot.mediaAssetId);
    if (!item?.url) return;
    const dur = itemDurationSec(item, slot.imageDisplayDurationSec);
    if (endsAtMs != null && cursorStartMs + dur * 1000 > endsAtMs) return;
    out.push({
      channelType: slot.channelType,
      itemId: item.id,
      startsAt: new Date(cursorStartMs).toISOString(),
      durationSec: dur,
    });
    return;
  }

  if (!playlist) return;
  const rotation = buildRotation(playlist, deterministicSeed);
  if (rotation.length === 0) return;

  // Anchor the rotation walk to the occupancy's REAL start (the unclipped
  // occurrenceStartsAt when the caller retained it, else the slot start). The
  // composed now-slot's startsAt equals the request instant, so without the
  // anchor every 5-minute poll re-aligned the group to rotation index 0.
  const anchorMs = slot.occurrenceStartsAt
    ? new Date(slot.occurrenceStartsAt).getTime()
    : slotStartMs;
  const elapsedMs = anchorMs < from.getTime() ? from.getTime() - anchorMs : 0;
  const { index: startIndex, offsetMs } = elapsedIntoRotation(
    rotation,
    elapsedMs,
    slot.imageDisplayDurationSec,
  );

  // The guard multiplier scales with the cap so a horizon-widened cap (which
  // needs many passes over a short rotation) is actually reachable, while a
  // degenerate rotation still terminates.
  const guardLimit = Math.max(rotation.length * 20, maxSegments * 2);
  // Start on the anchored grid: a segment already underway is emitted with
  // its true start (offsetMs before the request instant), so every device
  // that computes the timeline at ANY instant inside the occupancy derives
  // the identical segment boundaries and a mid-track join seeks mid-track.
  let cursorMs = cursorStartMs - offsetMs;
  let idx = startIndex;
  let guard = 0;
  while (out.length < maxSegments && guard < guardLimit) {
    guard += 1;
    const item = rotation[idx]!;
    const dur = itemDurationSec(item, slot.imageDisplayDurationSec);
    if (endsAtMs != null && cursorMs + dur * 1000 > endsAtMs) break;
    // Segments starting at/after the horizon end are useless to the device —
    // its cached payload is refreshed long before then.
    if (horizonEndMs != null && cursorMs >= horizonEndMs) break;
    out.push({
      channelType: slot.channelType,
      itemId: item.id,
      startsAt: new Date(cursorMs).toISOString(),
      durationSec: dur,
      rotationIndex: idx,
    });
    cursorMs += dur * 1000;
    idx = (idx + 1) % rotation.length;
    if (endsAtMs != null && cursorMs >= endsAtMs) break;
  }
}

/**
 * Project forward segment start times for active schedule slots so devices in
 * a sync group follow the same track order and clock alignment.
 *
 * The cap applies **per channel**: a multi-channel venue (music + ads + video)
 * must not let one channel's long rotation starve the others of timeline
 * horizon — with a shared global cap, the final sort could even drop a
 * channel's segments entirely.
 *
 * `horizonMs` (CB-091): offline syncs widen the cap via
 * `timelineSegmentCapForHorizon` and bound the projection to
 * `from + horizonMs` so the timeline covers the device's cached window
 * without emitting segments the device would refresh past anyway. Omitted
 * (online default) the projection is bounded only by the cap + slot end.
 */
export function buildSegmentTimeline(
  slots: TimelineSlot[],
  playlists: TimelinePlaylist[],
  deterministicSeed: string,
  from: Date,
  maxSegmentsPerChannel = MAX_TIMELINE_SEGMENTS,
  horizonMs?: number | null,
): RotationSyncSegment[] {
  const playlistById = new Map(playlists.map((p) => [p.id, p]));
  const byChannel = new Map<
    SyncSegment["channelType"],
    RotationSyncSegment[]
  >();
  const horizonEndMs =
    horizonMs != null && Number.isFinite(horizonMs) && horizonMs > 0
      ? from.getTime() + horizonMs
      : null;

  for (const slot of slots) {
    if (!slot.playlistId && !slot.mediaAssetId) continue;
    let bucket = byChannel.get(slot.channelType);
    if (!bucket) {
      bucket = [];
      byChannel.set(slot.channelType, bucket);
    }
    if (bucket.length >= maxSegmentsPerChannel) continue;
    const playlist = slot.playlistId
      ? (playlistById.get(slot.playlistId) ?? null)
      : slot.mediaAssetId
        ? (playlists.find((p) =>
            p.items.some((i) => i.id === slot.mediaAssetId),
          ) ?? null)
        : null;
    appendChannelSegments(
      bucket,
      slot,
      playlist,
      deterministicSeed,
      from,
      maxSegmentsPerChannel,
      horizonEndMs,
    );
  }

  const out = [...byChannel.values()].flat();
  out.sort((a, b) => a.startsAt.localeCompare(b.startsAt));
  return out;
}
